import * as React from 'react';
import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import * as RPNInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import i18n from '@/utils/i18n';

type CountryInputProps = {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
  triggerClassName?: string;
  buttonProps?: React.ComponentProps<typeof Button>;
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="tw:flex tw:items-center tw:justify-center tw:h-4 tw:w-6 tw:shrink-0 tw:overflow-hidden tw:rounded-sm tw:bg-foreground/20 tw:[&_svg]:size-full!">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

export function CountryInput({
  value,
  onChange,
  disabled,
  placeholder,
  triggerClassName,
  buttonProps,
}: CountryInputProps) {
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);

  const countryList = React.useMemo(() => {
    return RPNInput.getCountries().map((country) => ({
      value: country,
      label: RPNInput.getCountryCallingCode(country)
        ? new Intl.DisplayNames(['en'], { type: 'region' }).of(country) ||
          country
        : country,
    }));
  }, []);

  const selectedCountryLabel = value
    ? new Intl.DisplayNames(['en'], { type: 'region' }).of(value) || value
    : placeholder || i18n.t('Common:select');

  return (
    <Popover
      open={isOpen}
      modal
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) {
          setSearchValue('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          {...buttonProps}
          type="button"
          variant="outline"
          className={cn(
            'tw:flex tw:items-center tw:gap-2 tw:justify-between tw:font-normal',
            triggerClassName,
          )}
          disabled={disabled}
        >
          <span className="tw:flex-1 tw:text-start tw:overflow-hidden tw:text-nowrap tw:text-ellipsis tw:items-center tw:gap-2">
            {selectedCountryLabel}
          </span>
          <ChevronsUpDown className="tw:size-4 tw:opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="tw:w-[300px] tw:p-0">
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={(value) => {
              setSearchValue(value);
              setTimeout(() => {
                if (scrollAreaRef.current) {
                  const viewportElement = scrollAreaRef.current.querySelector(
                    '[data-radix-scroll-area-viewport]',
                  );
                  if (viewportElement) {
                    viewportElement.scrollTop = 0;
                  }
                }
              }, 0);
            }}
            placeholder={i18n.t('Common:search')}
          />
          <CommandList>
            <ScrollArea ref={scrollAreaRef} className="tw:h-72">
              <CommandEmpty>{i18n.t('ItemLocation:noResults')}</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value: country, label }) => (
                  <CommandItem
                    key={country}
                    className="tw:gap-2"
                    onSelect={() => {
                      onChange(country);
                      setIsOpen(false);
                    }}
                  >
                    <FlagComponent country={country} countryName={label} />
                    <span className="tw:flex-1 tw:text-sm">{label}</span>
                    <CheckIcon
                      className={cn(
                        'tw:ml-auto tw:size-4',
                        country === value ? 'tw:opacity-100' : 'tw:opacity-0',
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
